<template>
  <div v-if="token">

    <!-- Le bouton d'aide, toujours au même endroit. Il prend le visage d'Emma
         quand l'écran courant a une vidéo, sinon l'icône d'assistance. -->
    <div class="help-fab" :class="{ 'help-fab-visible': fabVisible }" @click="openPanel">
      <ion-img v-if="videoId" class="help-fab-emma" :src="apiroot + '/public/emma-pop.png'"></ion-img>
      <ion-icon v-else class="help-fab-icon" :icon="helpCircleOutline"></ion-icon>
      <span v-if="videoId" class="help-fab-dot"></span>
    </div>

    <!-- Une seule fois par appareil : on signale que l'assistance a déménagé. -->
    <div v-if="showHint" class="help-hint" @click="openPanel">
      <b>{{ _t("L'assistance est ici") }}</b>
      <p>{{ _t("Recherche, vidéos et tickets sont maintenant regroupés dans ce bouton.") }}</p>
      <span class="help-hint-ok" @click.stop="dismissHint">{{ _t("J'ai compris") }}</span>
    </div>

    <!-- Modale standard : l'app tourne avec animated:false, et les modales en
         « sheet » (breakpoints) ne se referment plus dans ce mode. -->
    <ion-modal
      class="help-modal"
      :is-open="open"
      @didDismiss="closePanel"
    >
      <ion-header :translucent="true">
        <ion-toolbar>
          <ion-title>{{ _t('Assistance') }}.</ion-title>
          <ion-buttons slot="end">
            <ion-button @click="closePanel">
              <ion-icon slot="icon-only" :icon="closeOutline"></ion-icon>
            </ion-button>
          </ion-buttons>
        </ion-toolbar>
        <!-- La recherche reste en tête : elle ne bouge pas quand le contenu
             change, et on peut chercher sans rien scroller. -->
        <ion-toolbar v-if="!videoMode" class="help-search-bar">
          <ion-searchbar
            class="help-search"
            mode="ios"
            v-model="helpQuery"
            :debounce="120"
            :placeholder="_t('Une question ? Tapez un mot-clé')"
            show-clear-button="focus"
          ></ion-searchbar>
        </ion-toolbar>
      </ion-header>

      <ion-content class="help-modal-content">

        <!-- Accueil : la première fois qu'un écran a une vidéo, elle occupe
             tout le panneau. Passée une fois, elle ne revient plus d'elle-même
             et se retrouve en simple ligne « Revoir la vidéo ». -->
        <div v-if="videoMode" class="help-intro">
          <div class="help-intro-video">
            <mux-player
              :key="videoId"
              :playback-id="videoId"
              stream-type="on-demand"
              controls
              autoplay
              playsinline
              @ended="skipVideo"
            />
          </div>
          <ion-button class="help-intro-skip" fill="clear" @click="skipVideo">
            {{ _t('Passer la vidéo') }}
            <ion-icon slot="end" :icon="arrowForwardOutline"></ion-icon>
          </ion-button>
        </div>

        <div v-else class="help-panel">

          <!-- Résultats de recherche -->
          <template v-if="isSearching">
            <ion-list class="help-list">
              <ion-item
                v-for="article in suggestions"
                :key="article.slug"
                color="white" :button="true" lines="full"
                @click="openArticle(article)"
              >
                <ion-icon slot="start" color="medium"
                  :icon="article.video ? playCircleOutline : documentTextOutline"></ion-icon>
                <ion-label class="ion-text-wrap">
                  <h4>{{ article.question }}</h4>
                  <p>{{ article.category_label }}</p>
                </ion-label>
                <ion-icon slot="end" color="medium" :icon="openOutline"></ion-icon>
              </ion-item>

              <ion-item v-if="!suggestions.length" color="white" lines="none">
                <ion-label class="ion-text-wrap">
                  <h4>{{ _t('Aucun article trouvé') }}</h4>
                  <p>{{ _t('Créez un ticket, notre équipe vous répond.') }}</p>
                </ion-label>
              </ion-item>

              <ion-item v-else color="white" :button="true" lines="none" @click="openSearch()">
                <ion-label class="ion-text-wrap" color="primary">
                  <p>{{ _t("Voir tous les résultats dans le centre d'aide") }}</p>
                </ion-label>
                <ion-icon slot="end" color="primary" :icon="openOutline"></ion-icon>
              </ion-item>
            </ion-list>
          </template>

          <!-- Vue par défaut : l'aide de l'écran courant, puis les tickets -->
          <template v-else>

            <template v-if="pageArticles.length">
              <p class="help-section">{{ _t('Pour cet écran') }}</p>
              <ion-list class="help-list">
                <ion-item
                  v-for="article in pageArticles"
                  :key="article.slug"
                  color="white" :button="true" lines="full"
                  @click="openArticle(article)"
                >
                  <ion-icon slot="start" color="medium" :icon="documentTextOutline"></ion-icon>
                  <ion-label class="ion-text-wrap">
                    <h4>{{ article.question }}</h4>
                    <p>{{ article.category_label }}</p>
                  </ion-label>
                  <ion-icon slot="end" color="medium" :icon="openOutline"></ion-icon>
                </ion-item>
              </ion-list>
            </template>

            <p class="help-section">{{ _t('vos tickets') }}</p>
            <ion-list class="help-list">
              <ion-item
                v-for="(ticket, index) in tickets.slice(0, 3)"
                :key="index"
                color="white" :button="true" lines="full"
                @click="goTo(ticket.path)"
              >
                <ion-icon slot="start" color="medium"
                  :icon="ticket.state === 'done' ? checkmarkCircleOutline : timeOutline"></ion-icon>
                <ion-label class="ion-text-wrap">
                  <h4>Ticket {{ ticket.name }}</h4>
                  <p>{{ _t(ticket.ticket_type_name) }}</p>
                </ion-label>
              </ion-item>

              <ion-item v-if="ticketsLoaded && !tickets.length" color="white" lines="full">
                <ion-label class="ion-text-wrap">
                  <p>{{ _t("vous n'avez pas de ticket en cours") }}</p>
                </ion-label>
              </ion-item>

              <ion-item v-if="!ticketsLoaded" color="white" lines="full">
                <ion-spinner name="dots"></ion-spinner>
              </ion-item>

              <ion-item color="white" :button="true" lines="none" @click="goTo('/sav')">
                <ion-icon slot="start" color="primary" :icon="addCircleOutline"></ion-icon>
                <ion-label color="primary" class="ion-text-wrap">
                  <p>{{ _t('Créer un ticket') }}</p>
                </ion-label>
              </ion-item>
            </ion-list>

            <ion-list class="help-list">
              <!-- La vidéo reste accessible, mais en ligne discrète. -->
              <ion-item v-if="videoId" color="white" :button="true" lines="full" @click="replayVideo()">
                <ion-icon slot="start" color="medium" :icon="playCircleOutline"></ion-icon>
                <ion-label class="ion-text-wrap">
                  <h4>{{ _t("Revoir la vidéo d'Emma") }}</h4>
                  <p>{{ _t('La présentation de cet écran') }}</p>
                </ion-label>
              </ion-item>

              <ion-item color="white" :button="true" lines="none" @click="openHelpHome()">
                <ion-icon slot="start" color="medium" :icon="bookOutline"></ion-icon>
                <ion-label class="ion-text-wrap">
                  <h4>{{ _t("Centre d'aide") }}</h4>
                  <p>{{ _t('Tous les guides et tutoriels') }}</p>
                </ion-label>
                <ion-icon slot="end" color="medium" :icon="openOutline"></ion-icon>
              </ion-item>
            </ion-list>

          </template>
        </div>
      </ion-content>
    </ion-modal>
  </div>
</template>

<script>
import '@mux/mux-player';
import axios from 'axios';
import { mapState } from 'vuex';
import {
  IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonContent,
  IonSearchbar, IonList, IonItem, IonLabel, IonIcon, IonImg, IonSpinner
} from '@ionic/vue';
import {
  closeOutline, openOutline, playCircleOutline, documentTextOutline,
  timeOutline, checkmarkCircleOutline, addCircleOutline, bookOutline, helpCircleOutline, arrowForwardOutline
} from 'ionicons/icons';
import _t from '@/components/TranslateComponent.vue';
import { helpForRoute } from '@/help/helpMap';

// Versionnée : le jour où l'aide déménage encore, passer à _v2 suffit à
// refaire apparaître la bulle chez tout le monde.
const HINT_KEY = 'helpHintSeen_v1';

// Les vidéos déjà passées, indexées par identifiant Mux : deux écrans qui
// partagent la même vidéo ne la reproposent donc pas deux fois.
const SEEN_VIDEOS_KEY = 'helpVideosSeen_v1';

export default {
  name: 'HelpCenter',

  components: {
    IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonContent,
    IonSearchbar, IonList, IonItem, IonLabel, IonIcon, IonImg, IonSpinner
  },

  mixins: [_t],

  setup() {
    return {
      closeOutline, openOutline, playCircleOutline, documentTextOutline,
      timeOutline, checkmarkCircleOutline, addCircleOutline, bookOutline, helpCircleOutline, arrowForwardOutline
    };
  },

  data() {
    return {
      apiroot: process.env.VUE_APP_ROOT_API,
      helproot: process.env.VUE_APP_HELP_URL || 'https://my.celestetic.com/help',
      open: false,
      fabVisible: false,
      showHint: false,
      hintDone: false,
      videoMode: false,
      helpQuery: '',
      helpIndex: [],
      helpMeta: null,
      tickets: [],
      ticketsLoaded: false
    };
  },

  computed: {
    ...mapState({
      token: state => state.token,
      lang: state => state.lang
    }),

    routeHelp() {
      return helpForRoute(this.$route && this.$route.name);
    },

    videoId() {
      const video = this.routeHelp.video;
      return (video && video[this.lang]) || null;
    },

    isSearching() {
      return this.helpQuery.trim().length > 0;
    },

    /** Les articles rattachés à l'écran courant, résolus dans la langue du compte. */
    pageArticles() {
      const keys = this.routeHelp.articles || [];
      return keys
        .map(key => this.helpIndex.find(article => article.key === key))
        .filter(Boolean);
    },

    /**
     * Filtrage local sur l'index déjà chargé : pas de requête par frappe.
     * Pondération reprise de la recherche du centre d'aide.
     */
    suggestions() {
      const query = this.helpNormalize(this.helpQuery);
      if (query.length < 2 || !this.helpIndex.length) return [];

      const terms = query.split(/[^a-z0-9+]+/).filter(term => term.length >= 2);
      if (!terms.length) return [];

      const scored = [];

      for (const article of this.helpIndex) {
        let score = 0;
        let matchedAll = true;

        for (const term of terms) {
          let termScore = 0;
          if (article._question.includes(term)) termScore += 10;
          if (article._title.includes(term)) termScore += 8;
          if (article._keywords.includes(term)) termScore += 6;
          if (article._summary.includes(term)) termScore += 2;
          if (!termScore) matchedAll = false;
          score += termScore;
        }

        if (!score) continue;
        scored.push({ article, score: matchedAll ? score * 2 : score });
      }

      scored.sort((a, b) => b.score - a.score);

      return scored.slice(0, 8).map(row => row.article);
    }
  },

  created() {
    this.loadHelpIndex();
  },

  mounted() {
    // Même temporisation que l'ancienne pastille Emma : le bouton se pose une
    // fois la page en place, pas pendant son rendu.
    setTimeout(() => {
      this.fabVisible = true;
      this.maybeShowHint();
    }, 1500);
  },

  watch: {
    lang() {
      this.loadHelpIndex();
    },

    // Le composant est monté avant la connexion : sans ça, la bulle serait
    // décidée alors qu'il n'y a pas encore de compte, donc jamais montrée.
    token(value) {
      if (value) this.maybeShowHint();
    }
  },

  methods: {

    /** Bulle de première visite : une seule fois par appareil, et seulement connecté. */
    maybeShowHint() {
      if (!this.token || this.hintDone || localStorage.getItem(HINT_KEY)) return;
      this.hintDone = true;
      this.showHint = true;
      setTimeout(() => { this.showHint = false; }, 10000);
    },

    /** Langue du centre d'aide : fr, en ou nl, depuis le fr_BE / nl_BE / en_US du compte. */
    helpLang() {
      const supported = ['fr', 'en', 'nl'];
      const lang = (this.lang || 'fr_BE').slice(0, 2).toLowerCase();
      return supported.includes(lang) ? lang : 'fr';
    },

    helpNormalize(text) {
      return (text || '').toString().toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    },

    /**
     * Index léger du centre d'aide (~12 ko). Servi d'abord depuis le cache pour
     * que les suggestions soient immédiates, puis rafraîchi en arrière-plan.
     */
    async loadHelpIndex() {
      const lang = this.helpLang();
      const cacheKey = 'helpIndex_' + lang;

      try {
        const cached = JSON.parse(localStorage.getItem(cacheKey) || 'null');
        if (cached && cached.data) this.setHelpIndex(cached.data);
        if (cached && cached.time && Date.now() - cached.time < 86400000) return;
      } catch (error) {
        console.log('help index cache', error);
      }

      try {
        const response = await axios.get(this.helproot + '/' + lang + '/index.json');
        this.setHelpIndex(response.data);
        localStorage.setItem(cacheKey, JSON.stringify({ time: Date.now(), data: response.data }));
      } catch (error) {
        console.log('help index', error);
      }
    },

    setHelpIndex(data) {
      if (!data || !Array.isArray(data.articles)) return;

      this.helpMeta = data;
      // Pré-normalisé une fois : le filtrage par frappe se réduit à des includes().
      this.helpIndex = data.articles.map(article => ({
        ...article,
        _title: this.helpNormalize(article.title),
        _question: this.helpNormalize(article.question),
        _summary: this.helpNormalize(article.summary),
        _keywords: this.helpNormalize((article.keywords || []).join(' '))
      }));
    },

    openPanel() {
      this.dismissHint();
      this.helpQuery = '';
      // La vidéo de l'écran n'est proposée d'office qu'une fois.
      this.videoMode = !!this.videoId && !this.seenVideos()[this.videoId];
      this.open = true;
      if (!this.ticketsLoaded) this.loadTickets();
    },

    closePanel() {
      this.open = false;
      this.videoMode = false;
    },

    seenVideos() {
      try {
        return JSON.parse(localStorage.getItem(SEEN_VIDEOS_KEY) || '{}') || {};
      } catch (error) {
        return {};
      }
    },

    /** Fin ou saut de la vidéo : on ne la repropose plus, elle reste en ligne. */
    skipVideo() {
      if (this.videoId) {
        const seen = this.seenVideos();
        seen[this.videoId] = 1;
        localStorage.setItem(SEEN_VIDEOS_KEY, JSON.stringify(seen));
      }
      this.videoMode = false;
    },

    replayVideo() {
      this.helpQuery = '';
      this.videoMode = true;
    },

    dismissHint() {
      this.showHint = false;
      localStorage.setItem(HINT_KEY, '1');
    },

    async loadTickets() {
      try {
        const response = await axios.post(
          process.env.VUE_APP_ROOT_API + '/sav/list',
          {},
          { headers: { Authorization: `Bearer ${this.token}` } }
        );
        this.tickets = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error('help tickets', error);
      } finally {
        this.ticketsLoaded = true;
      }
    },

    openArticle(article) {
      window.open(article.url, '_blank');
    },

    openSearch() {
      const base = (this.helpMeta && this.helpMeta.search_url)
        || this.helproot + '/' + this.helpLang() + '/search';
      window.open(base + '?q=' + encodeURIComponent(this.helpQuery.trim()), '_blank');
    },

    openHelpHome() {
      const home = (this.helpMeta && this.helpMeta.home)
        || this.helproot + '/' + this.helpLang() + '/';
      window.open(home, '_blank');
    },

    goTo(path) {
      this.closePanel();
      if (path) this.$router.push({ path });
    }
  }
};
</script>

<style scoped>
.help-fab {
  position: fixed;
  top: calc(env(safe-area-inset-top, 0px) + 4px);
  right: 12px;
  width: 40px;
  height: 40px;
  z-index: 150;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transform: scale(0.8);
  transition: opacity .4s ease-in-out, transform .4s ease-in-out;
}

.help-fab-visible {
  opacity: 1;
  transform: scale(1);
}

.help-fab-emma {
  width: 32px;
  height: 32px;
  pointer-events: none;
}

/* Une ion-icon, pas une .app-icon : ces dernières sont des SVG en background
   avec un filter:invert() en mobile pour la barre noire, inutilisable ici. */
.help-fab-icon {
  font-size: 26px;
  color: var(--ion-color-dark, #222428);
  pointer-events: none;
}

/* Le point signale qu'une vidéo existe pour cet écran. */
.help-fab-dot {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--ion-color-primary, #000);
  border: 2px solid #fff;
}

.help-hint {
  position: fixed;
  top: calc(env(safe-area-inset-top, 0px) + 50px);
  right: 12px;
  z-index: 150;
  max-width: 250px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #000;
  color: #fff;
  box-shadow: 0 6px 20px rgba(0, 0, 0, .25);
  cursor: pointer;
}

.help-hint::after {
  content: '';
  position: absolute;
  top: -6px;
  right: 14px;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 6px solid #000;
}

.help-hint b {
  font-size: 14px;
}

.help-hint p {
  margin: 4px 0 8px 0;
  font-size: 12px;
  color: #ddd;
}

.help-hint-ok {
  font-size: 12px;
  text-decoration: underline;
}

.help-modal {
  --border-radius: 16px;
}

.help-modal-content {
  --background: #f4f5f8;
}

/* Une seule gouttière pour tout le panneau : la recherche, les titres de
   section et les blocs partagent le même bord gauche. */
.help-panel,
.help-intro,
.help-modal ion-toolbar.help-search-bar {
  --help-gutter: 16px;
}

.help-panel,
.help-intro {
  padding: 12px var(--help-gutter) calc(24px + env(safe-area-inset-bottom, 0px));
}

/* La gouttière est portée par la toolbar, pas par la searchbar : sinon les
   paddings internes d'Ionic s'ajoutent et le champ se décale des blocs. */
.help-modal ion-toolbar.help-search-bar {
  padding-inline: 0;
  --padding-start: var(--help-gutter, 16px);
  --padding-end: var(--help-gutter, 16px);
  --padding-top: 0;
  --padding-bottom: 8px;
  --min-height: 0;
}

.help-search {
  --background: #fff;
  --box-shadow: none;
  --border-radius: 12px;
  padding: 0;
}

/* Accueil vidéo : elle occupe le panneau, on la passe pour accéder au reste. */
.help-intro {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;
}

.help-intro-video {
  width: 100%;
  max-width: 420px;
  aspect-ratio: 1 / 1;
  border-radius: 16px;
  overflow: hidden;
  background: #000;
}

.help-intro-video mux-player {
  width: 100%;
  height: 100%;
}

.help-intro-skip {
  margin-top: 10px;
  --color: #6b6b6b;
  font-size: 14px;
  text-transform: none;
}

.help-section {
  margin: 18px 0 6px 0;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: #8c8c8c;
}

/* Chaque groupe est une carte : il lui faut de l'air, sinon deux listes
   successives se lisent comme une seule. */
.help-list {
  border-radius: 12px;
  overflow: hidden;
  padding: 0;
  margin-bottom: 14px;
}

.help-list ion-item {
  --padding-start: 14px;
  --inner-padding-end: 10px;
}

/* Le dernier item ne porte pas de séparateur : sinon la ligne dépasse de
   l'arrondi de la carte. */
.help-list ion-item:last-of-type {
  --border-width: 0;
  --inner-border-width: 0;
}

.help-list ion-item h4 {
  font-size: 15px;
  margin-bottom: 3px;
}

.help-list ion-item p {
  font-size: 12px;
}

/* Le premier groupe suit directement la recherche : pas de titre au-dessus,
   donc pas de marge haute non plus. */
.help-panel > .help-list:first-child {
  margin-top: 2px;
}

@media (min-width: 720px) {
  .help-panel,
  .help-intro,
  .help-modal ion-toolbar.help-search-bar {
    --help-gutter: 20px;
  }
}

.help-fade-enter-active,
.help-fade-leave-active {
  transition: opacity .2s ease, max-height .25s ease, margin .25s ease;
}

.help-fade-enter-from,
.help-fade-leave-to {
  opacity: 0;
  max-height: 0;
  margin-bottom: 0;
}
</style>
